/*
 * page-background.js — parallax for the Page Background.
 * ------------------------------------------------------
 * On scroll it can move two things independently (behind the fixed lens):
 *   FILL    — translates the fill (transform = GPU-composited, smooth on Safari;
 *             background-position would repaint every frame). The fill is sized
 *             tall enough to cover the WHOLE parallax travel for the page, so the
 *             upward shift never reveals a gap; its repeat-y texture tiles into
 *             the extra height (background-size is width-relative, so tile size
 *             is unchanged). Recomputed on resize / content-height changes.
 *   OVERLAY — shifts via the independent `translate` property (--cr-ov-py),
 *             which composes with its anchor `transform`.
 *
 * Usage:
 *   <cr-page-background
 *     data-fill-speed="100" data-overlay-speed="40"
 *     data-parallax-devices="both|desktop|mobile">
 * Speeds are a percentage of scroll (0 = static). data-parallax-devices limits
 * which viewports parallax. Honors prefers-reduced-motion; rAF-gated.
 */
class CrPageBackground extends HTMLElement {
  connectedCallback() {
    this.fill = this.querySelector('.cr-fill');
    this.overlay = this.querySelector('.cr-overlay');
    this.fillSpeed = (parseFloat(this.getAttribute('data-fill-speed')) || 0) / 100;
    this.overlaySpeed = (parseFloat(this.getAttribute('data-overlay-speed')) || 0) / 100;
    this.devices = this.getAttribute('data-parallax-devices') || 'both';

    // When the gradient map is on, the fill/overlay are inside the filtered
    // .cr-stage. Safari breaks an SVG filter if a child gets its own compositing
    // layer, so for a stage child we avoid transforms and parallax via
    // background-position (filter-safe). But Safari re-renders the whole filter
    // each frame, so that's choppy there — so on Safari we leave a stage child
    // STATIC (smooth, gradient map intact). Outside a stage, transform is used
    // (smooth everywhere).
    this.fillInStage = !!(this.fill && this.fill.closest('.cr-stage'));
    this.overlayInStage = !!(this.overlay && this.overlay.closest('.cr-stage'));
    this.isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent || '');
    if (this.isSafari) {
      if (this.fillInStage) this.fillSpeed = 0;
      if (this.overlayInStage) this.overlaySpeed = 0;
    }

    var hasFill = this.fill && this.fillSpeed;
    var hasOverlay = this.overlay && this.overlaySpeed;
    if (!hasFill && !hasOverlay) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.mqMobile = window.matchMedia('(max-width: 749px)');
    this.ticking = false;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleChange = this.handleChange.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.mqMobile.addEventListener('change', this.handleChange);

    // Dynamic fill sizing is only needed for the transform path (the element
    // moves). The stage/background-position path tiles infinitely on a fixed
    // box, so it needs no oversizing.
    if (hasFill && !this.fillInStage) {
      this.sizeFill = this.sizeFill.bind(this);
      this.sizeFill();
      window.addEventListener('resize', this.sizeFill, { passive: true });
      if ('ResizeObserver' in window) {
        // Re-size when the page (content) height changes, e.g. async content.
        this.resizeObserver = new ResizeObserver(this.sizeFill);
        this.resizeObserver.observe(document.body);
      }
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
    if (this.mqMobile && this.handleChange) {
      this.mqMobile.removeEventListener('change', this.handleChange);
    }
    if (this.sizeFill) window.removeEventListener('resize', this.sizeFill);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  // Tall enough to cover the viewport PLUS the full upward travel for this page
  // (scrollable height x speed), with a little slack. The fill is position:fixed,
  // so changing its height never feeds back into the page's scrollHeight.
  sizeFill() {
    var vh = window.innerHeight;
    var scrollable = Math.max(0, document.documentElement.scrollHeight - vh);
    var travel = scrollable * this.fillSpeed;
    this.fill.style.height = Math.ceil(vh + travel + vh * 0.25) + 'px';
  }

  // Is parallax enabled for the current viewport?
  activeForViewport() {
    if (this.devices === 'desktop') return !this.mqMobile.matches;
    if (this.devices === 'mobile') return this.mqMobile.matches;
    return true; // both
  }

  handleChange() {
    this.update(); // re-evaluate (and reset) on the desktop/mobile breakpoint
  }

  handleScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    if (!this.activeForViewport()) {
      this.reset();
      return;
    }
    // Negative so things travel UP as the page scrolls down (passing by).
    var y = window.scrollY;
    if (this.fill && this.fillSpeed) {
      if (this.fillInStage) {
        // Filter-safe (no layer promotion) so the gradient map keeps working.
        this.fill.style.backgroundPositionY = -(y * this.fillSpeed) + 'px';
      } else {
        this.fill.style.transform = 'translate3d(0,' + -(y * this.fillSpeed) + 'px,0)';
      }
    }
    if (this.overlay && this.overlaySpeed) {
      this.overlay.style.setProperty('--cr-ov-py', -(y * this.overlaySpeed) + 'px');
    }
    // Publish the scene's parallax offset globally so OTHER components pinned
    // to the scene (Scene Accents) can ride along and stay aligned with the
    // art they were placed on. Accents target the overlay art when it moves,
    // else the fill. Zero-speed configs never set it (stays at the CSS 0px
    // default).
    var sceneSpeed = this.overlay && this.overlaySpeed ? this.overlaySpeed : this.fillSpeed;
    if (sceneSpeed) {
      document.documentElement.style.setProperty('--cr-scene-py', -(y * sceneSpeed) + 'px');
    }
  }

  // Clear inline parallax so the CSS rest position applies (a static overlay
  // offset, set via --cr-ov-py's default in the stylesheet, is unaffected).
  reset() {
    if (this.fill) {
      this.fill.style.transform = '';
      this.fill.style.backgroundPositionY = '';
    }
    if (this.overlay) this.overlay.style.removeProperty('--cr-ov-py');
    document.documentElement.style.removeProperty('--cr-scene-py');
  }
}

if (!customElements.get('cr-page-background')) {
  customElements.define('cr-page-background', CrPageBackground);
}
